import { ClickAwayListener } from "@nova/component-click-away-listener"
import classNames from "classnames"
import { animationClass } from "features/data-management/workspace/constants"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect, useRef, useState } from "react"
import { selectMapSearchIsActive } from "./selectors"
import { setMapSearchIsActive } from "./slice"
import { Coordinate } from "@nova/protobuf"
import { useMapControl } from "../control/useMapControl"
import { mapSearchKeywords, mapSearchLargestKeyword } from "./constants"
import { useCyclingText } from "./useCyclingText"
import { useMapSearch } from "./useMapSearch"
import { MapSearchResults } from "./MapSearchResults"
import { useMapBackground } from "../control/useMapStyles"

export const MapSearch = () => {
    const dispatch = useAppDispatch()
    const searchRef = useRef<HTMLInputElement>(null)
    const [previousKeyword, cyclingKeyword, isTransitioningKeyword] = useCyclingText(
        2000,
        mapSearchKeywords
    )
    const formmattedCoordinate = useFormattedMouseCoordinate() ?? `Search for a ${cyclingKeyword}`
    const { isLight } = useMapBackground()

    const isActive = useAppSelector(selectMapSearchIsActive)
    const { handleSelect, handleInputChange, searchContents, concatenatedResults } = useMapSearch()
    const hasContents = searchContents !== undefined && searchContents.length > 0

    useEffect(() => {
        const isFocused = document.activeElement === searchRef.current

        if (isActive && !isFocused) searchRef.current?.focus()
        else if (!isActive) {
            handleInputChange(undefined)
            searchRef.current?.blur()
        }
    }, [isActive, handleInputChange])

    useEffect(() => {
        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isActive) {
                dispatch(setMapSearchIsActive(false))
                handleInputChange(undefined)
                e.stopPropagation()
            }
        }

        document.addEventListener("keydown", handleKeydown)

        return () => {
            document.removeEventListener("keydown", handleKeydown)
        }
    }, [dispatch, handleInputChange, isActive])

    return (
        <ClickAwayListener
            className={classNames("flex-1 absolute bottom-0", { "w-full": isActive })}
            onClickAway={() => dispatch(setMapSearchIsActive(false))}
        >
            {isActive && (
                <MapSearchResults handleSelect={handleSelect} results={concatenatedResults} />
            )}

            <div
                className={classNames(
                    "h-9 pl-2.5 pr-1.5 flex gap-2 items-center rounded-lg border group duration-300 ease-out cursor-pointer",
                    { "pr-3 border-transparent": !hasContents && !isActive },
                    { "border-blue-500": hasContents || isActive },
                    isLight
                        ? {
                              "bg-white-800 text-gray-900": hasContents || isActive,
                              "text-gray-700 hover:text-gray-500": !hasContents && !isActive,
                          }
                        : {
                              "bg-black-800 text-gray-0": hasContents || isActive,
                              "text-gray-50 hover:text-gray-0": !hasContents && !isActive,
                              "text-gray-100": !hasContents && isActive,
                          }
                )}
                onClick={() => dispatch(setMapSearchIsActive(true))}
            >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 duration-300 ease-out">
                    <Icons.SmallIcon
                        Icon={Icons.MagnifyingGlass}
                        className={classNames(
                            {
                                "text-gray-0": isActive || hasContents,
                            },
                            isLight
                                ? {
                                      "text-gray-800 group-hover:text-gray-500":
                                          !isActive && !hasContents,
                                      "text-gray-900": isActive || hasContents,
                                  }
                                : {
                                      "text-gray-50 group-hover:text-gray-0":
                                          !isActive && !hasContents,
                                      "text-gray-0": isActive || hasContents,
                                  }
                        )}
                    />
                </div>

                {!isActive ? (
                    <h5 className="flex-grow min-w-0 overflow-hidden whitespace-nowrap text-ellipsis select-none mono">
                        {hasContents ? searchContents : formmattedCoordinate}
                    </h5>
                ) : (
                    <>
                        <input
                            className={classNames(
                                "p-0 bg-transparent border-0 !shadow-none min-w-0 flex-grow",
                                "focus:outline-none focus:border-transparent focus:ring-0",
                                "text-sm", // <small> text style
                                "placeholder:text-gray-500"
                            )}
                            ref={searchRef}
                            type="text"
                            value={searchContents || ""}
                            onChange={(e) => {
                                const sanitizedContents = e.target.value.trimStart()
                                handleInputChange(sanitizedContents)
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault()
                            }}
                        />

                        {!hasContents && (
                            <div
                                className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-gray-500 flex items-center overflow-hidden"
                                style={{ height: "calc(100% - 2px)" }}
                            >
                                {"Search for a "}
                                <span className="relative inline-block h-full" style={{ top: 8.5 }}>
                                    <span className="invisible">{mapSearchLargestKeyword}</span>
                                    <span
                                        key={`old-${previousKeyword}`}
                                        className="absolute top-0 left-1 transition-all duration-300 ease-out"
                                        style={{
                                            transform: !isTransitioningKeyword
                                                ? "translateY(160%)"
                                                : undefined,
                                        }}
                                    >
                                        {previousKeyword}
                                    </span>
                                    &nbsp;
                                    <span
                                        key={`new-${cyclingKeyword}`}
                                        className={classNames(
                                            "absolute top-0 left-1 transition-all duration-300 ease-out",
                                            {
                                                "-translate-y-[160%]": isTransitioningKeyword,
                                                "translate-y-0 opacity-100":
                                                    !isTransitioningKeyword,
                                            }
                                        )}
                                    >
                                        {cyclingKeyword}
                                    </span>
                                </span>
                            </div>
                        )}
                    </>
                )}

                {hasContents && (
                    <div
                        className={classNames(
                            "flex-shrink-0 rounded-sm text-gray-100 cursor-pointer w-5 h-5 flex items-center justify-center mr-px",
                            {
                                "hover:text-gray-500": isLight,
                                "hover:bg-gray-700 hover:text-gray-0": !isLight,
                            },
                            animationClass
                        )}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleInputChange("")
                        }}
                    >
                        <div className="absolute">
                            <Icons.ExtraSmallIcon Icon={Icons.XClose} />
                        </div>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    )
}

export const useFormattedMouseCoordinate = () => {
    const { unproject } = useMapControl()
    const [mouseCoordinate, setMouseCoordinate] = useState<Coordinate>()
    const formmattedCoordinate = mouseCoordinate
        ? `${mouseCoordinate.latitude.toFixed(6)}º, ${mouseCoordinate.longitude.toFixed(6)}º`
        : null

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if ((e.target as HTMLElement)?.className === "maplibregl-canvas") {
                const [longitude, latitude] = unproject([e.offsetX, e.offsetY])
                setMouseCoordinate({ longitude, latitude })
            }
        }

        document.addEventListener("mousemove", handleMouseMove)

        return () => {
            document.removeEventListener("mousemove", handleMouseMove)
        }
    }, [unproject])

    return formmattedCoordinate
}
